import styled, { keyframes } from 'styled-components';
import { theme } from '../../../styles/theme';

// Fidelidade ao DC: docs/claude-design/project/Aprovar Medico Portal Senne.dc.html
// (adaptado ao formato de MODAL — decisão do dono — em coluna única).
const c = theme.colors;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { transform: translateY(30px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

export const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(37, 37, 37, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100;
  padding: 24px;
  box-sizing: border-box;
  animation: ${fadeIn} 0.3s ease-out;
`;

export const ModalContainer = styled.div`
  width: 100%;
  max-width: 560px;
  background-color: ${c.surface};
  border-radius: ${theme.radii.lg};
  box-shadow: 0 16px 48px rgba(37, 37, 37, 0.24);
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  overflow: hidden;
  animation: ${slideUp} 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94);
`;

export const ModalBody = styled.div`
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  overflow-y: auto;
`;

export const ModalHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const ModalTitle = styled.h2`
  font-size: 22px;
  font-weight: 700;
  color: ${c.fg1};
  margin: 0;
`;

export const ModalSubtitle = styled.p`
  font-size: 16px;
  color: ${c.fg2};
  margin: 0;
`;

export const IdentityRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

export const Avatar = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: ${c.tintOrange50};
  color: ${c.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  font-weight: 700;
  flex: none;
`;

export const IdentityCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

export const IdentityName = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: ${c.fg1};
`;

export const PendingPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-height: 28px;
  padding: 3px 12px;
  border-radius: ${theme.radii.pill};
  font-size: 14px;
  font-weight: 600;
  background: ${c.warningSoft};
  color: ${c.warning};
  align-self: flex-start;

  &::before {
    content: '';
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${c.warning};
    flex: none;
  }
`;

export const Divider = styled.div`
  height: 1px;
  background: ${c.border};
`;

export const SectionTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: ${c.fg1};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;

  &::before {
    content: '';
    width: 4px;
    height: 18px;
    border-radius: 2px;
    background: ${c.primary};
    flex: none;
  }
`;

export const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const FieldLabel = styled.label`
  font-size: 16px;
  font-weight: 600;
  color: ${c.fg1};
`;

export const SelectHint = styled.div`
  font-size: 14px;
  color: ${c.fg3};
`;

const CMP_COLS = '96px 1fr 1fr 36px';

export const CompareTable = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${c.border};
  border-radius: ${theme.radii.sm};
  overflow: hidden;
`;

export const CompareHead = styled.div`
  display: grid;
  grid-template-columns: ${CMP_COLS};
  column-gap: 12px;
  background: ${c.gray50};
  padding: 0 16px;
  min-height: 44px;
  align-items: center;
  font-size: 14px;
  font-weight: 700;
  color: ${c.fg2};
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

export const CompareRow = styled.div`
  display: grid;
  grid-template-columns: ${CMP_COLS};
  column-gap: 12px;
  padding: 0 16px;
  min-height: 52px;
  align-items: center;
  font-size: 16px;
  border-top: 1px solid ${c.gray100};

  & > * {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const CompareField = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${c.fg2};
`;

export const CompareTasy = styled.div<{ $ok?: boolean }>`
  color: ${({ $ok }) => ($ok ? c.fg2 : c.danger)};
  font-weight: ${({ $ok }) => ($ok ? 400 : 600)};
`;

export const CompareIcon = styled.div<{ $ok?: boolean }>`
  display: flex;
  justify-content: center;
  color: ${({ $ok }) => ($ok ? c.success : c.danger)};
`;

export const WarnBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 16px;
  background: ${c.warningSoft};
  border: 1px solid #fde68a;
  border-radius: ${theme.radii.sm};

  & > svg {
    color: ${c.warning};
    flex: none;
    margin-top: 2px;
  }
  & > span {
    font-size: 14px;
    font-weight: 600;
    color: ${c.warning};
    line-height: 1.55;
  }
`;

export const ModalFooter = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding: 20px 32px;
  border-top: 1px solid ${c.border};
`;

export const SecondaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 48px;
  padding: 0 20px;
  border: 1.5px solid ${c.primary};
  border-radius: ${theme.radii.md};
  background: ${c.surface};
  color: ${c.primary};
  font-family: inherit;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: ${c.tintOrange50};
  }
`;

export const ApproveBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 48px;
  padding: 0 20px;
  border: none;
  border-radius: ${theme.radii.md};
  background: ${c.success};
  color: #fff;
  font-family: inherit;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.15s;

  &:hover {
    opacity: 0.92;
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;
